import { useCallback } from 'react';
import type { AccueilDataType } from '@/types';
import {
  useCyclesData,
  useEncaisseData,
  useIndicateursData,
  useRafraichirIndicateurs,
  useRecuStatsData,
} from './accueil';
import useAutoRefresh from './useAutoRefresh';

/**
 * Hook orchestrateur qui agrège toutes les données de l'accueil
 * Utilise les stores Zustand avec cache pour optimiser les performances
 * Maintient la compatibilité avec l'API existante
 */
const useAccueilData = (): AccueilDataType => {
  // Récupération des données via les hooks spécialisés
  const { encaisse, loading: loadingEncaisse, refresh: refreshEncaisse } = useEncaisseData();
  const {
    indicateurs,
    loading: loadingIndicateurs,
    refresh: refreshIndicateurs,
  } = useIndicateursData();
  const { cycles, loading: loadingCycles, refresh: refreshCycles } = useCyclesData();
  const { recuStats, loading: loadingRecuStats, refresh: refreshRecuStats } = useRecuStatsData();

  // Fonction de refresh global
  const refresh = useCallback(() => {
    refreshEncaisse();
    refreshIndicateurs();
    refreshCycles();
    refreshRecuStats();
  }, [refreshEncaisse, refreshIndicateurs, refreshCycles, refreshRecuStats]);

  // Rafraîchissement automatique : à l'entrée sur l'écran, périodiquement tant que l'onglet est
  // visible, et au retour sur l'onglet
  const { rafraichir, pret } = useRafraichirIndicateurs();
  useAutoRefresh(rafraichir, pret);

  return {
    encaisse,
    indicateurs,
    cycles,
    recuStats,
    loading: {
      encaisse: loadingEncaisse,
      indicateurs: loadingIndicateurs,
      cycles: loadingCycles,
      recuStats: loadingRecuStats,
    },
    refresh,
  };
};

export default useAccueilData;
